import React, { useState } from 'react';
import { format } from 'date-fns';
import { Link, useHistory, useLocation, useRouteMatch } from 'react-router-dom';
import { Box, Button, Text, TextButton } from '@zillow/constellation-10';
import { Col, Container, Row } from '@zg-rentals/zrs-comet';
import { parseQueryString } from '@zg-rentals/string-utils';
import {
  AuthCheckWrapper,
  HeadWrapper,
  LoadingConstants,
  numberUtils,
  useRentalManagerUser,
  useRmwPaths,
} from '@zg-rentals/rental-manager-env';
import type { ColumnConfig } from '@zg-rentals/advanced-table';
import { AdvancedTable } from '@zg-rentals/advanced-table';
import { PageWrapper } from '@zg-rentals/rental-revenue-components';

// Constants
import BillingError from '../../components/BillingError';
import SpinnerContainer from '../../components/SpinnerContainer';
import PaymentInfoModal from '../../components/modals/PaymentInfoModal';
import BillingPortalMaintenanceBanner from '../../components/BillingPortalMaintenanceBanner';
import billingUtils from '../../utils/billingUtils';
import { withErrorBoundary } from '../../components/withErrorBoundry';
import { useInvoiceDownloader } from '../../hooks/useInvoiceDownloader';
import { downloadRocketshipCsv, downloadSingleFamilyLegacyCsv } from '../../utils/downloadUtils';
import { useInvoice } from '../../hooks/useInvoice';
import { useAppMaintenance } from '../../hooks/useMaintenance';

import { SfhBreakdownTable } from './SfhBreakdownTable';
import { ListingSpotlightTable } from './ListingSpotlightTable';
import { InvoiceItemDetailsTable } from './InvoiceItemDetailsTable';
import { StyledBalanceCol, StyledContainer, StyledSummaryBox } from './StyledComponents';
import { InvoicePaymentDisplay } from '../../components/InvoicePaymentDisplay';
import { type AdproductsZuoraInvoicePaymentDto, ZuoraInvoiceType } from '@zg-rentals/ts-adproducts-client';
import { emitViewRentalsBillingInvoiceEvent, EVENT_IDS } from '../../model/ClickstreamEvents';
import { IconChevronLeftFilled } from '@zillow/constellation-icons';
import { localDateToDate } from '../../utils/dateUtils';

const DEFAULT_PAGE_TITLE = 'Invoice';

const formatDate = (value: string | number) => value && format(new Date(value), 'MM/dd/yyyy');

const InvoicePage = () => {
  const match = useRouteMatch();
  // @ts-ignore
  const invoiceId: string = match.params.invoiceId;
  const { user } = useRentalManagerUser();
  const {
    invoice: currentInvoice,
    errorMessage: currentInvoiceErrorMessage,
    loadingState: currentInvoiceLoadingState,
    fetchInvoice,
  } = useInvoice();

  const { isMaintenanceMode } = useAppMaintenance();
  const location = useLocation();
  const queryParams = parseQueryString(location.search);
  const isSingleFamilyLegacy =
    currentInvoice &&
    (currentInvoice.invoiceType === ZuoraInvoiceType.SINGLE_FAMILY_REIT ||
      currentInvoice.invoiceType === ZuoraInvoiceType.SINGLE_FAMILY_FLAT_FEE);
  const paymentsEnabled = user.features.canAccessInvoicePayments;
  const paths = useRmwPaths();
  const [showPaymentInfoModal, setShowPaymentInfoModal] = useState(false);
  const { getInvoices } = useInvoiceDownloader();
  const history = useHistory();

  const historyColumnConfig: Array<ColumnConfig<AdproductsZuoraInvoicePaymentDto>> = [
    {
      width: 3,
      header: 'Payment date',
      renderer: (value: AdproductsZuoraInvoicePaymentDto) => formatDate(value.createdTime),
      align: 'center',
    },
    {
      width: 3,
      header: 'Amount paid',
      renderer: (value: AdproductsZuoraInvoicePaymentDto) => numberUtils.asCurrency(value.appliedPaymentAmount),
      align: 'center',
    },
    {
      width: 3,
      header: 'Payment number',
      dataKey: 'paymentNumber',
      align: 'center',
    },
    {
      width: 3,
      header: 'Transaction status',
      dataKey: 'status',
      align: 'center',
      renderer: (value) => <InvoicePaymentDisplay value={value} />,
    },
  ];

  if (paymentsEnabled) {
    historyColumnConfig.splice(1, 0, {
      width: 3,
      header: 'Payment method',
      align: 'center',
      renderer: billingUtils.formatPaymentMethod,
    });
    historyColumnConfig.push({
      width: 3,
      header: 'Paid by',
      align: 'center',
      renderer: (value) => (value.email ? value.email : '-'),
    });
  }

  const handleEditClick = (accountNumber: string) => {
    const url = `${paths.billingPage}/account/${accountNumber}`;
    const target = `${url}?prev=invoice&invoiceNumber=${invoiceId}`;
    history.push(target);
  };

  React.useEffect(() => {
    window.scroll(0, 0);
    fetchInvoice(invoiceId);
    emitViewRentalsBillingInvoiceEvent({ user });
  }, []);

  const handlePayInfo = () => setShowPaymentInfoModal(true);

  const renderPaymentHistory = () => {
    if (!currentInvoice || currentInvoice.invoicePayments.length === 0) return null;
    return (
      <StyledContainer>
        <Text className="InvoicePage-card-heading" textStyle="heading-sm">
          Payment history
        </Text>
        <Box css={{ margin: 'xs' }} />
        <AdvancedTable
          columnConfig={historyColumnConfig}
          data={currentInvoice.invoicePayments
            .slice()
            .sort(
              (a: AdproductsZuoraInvoicePaymentDto, b: AdproductsZuoraInvoicePaymentDto) =>
                b.createdTime - a.createdTime,
            )}
        />
        <Box css={{ paddingBottom: 'xs' }} />
      </StyledContainer>
    );
  };

  const renderInvoiceDetails = () => {
    if (!currentInvoice) return;
    return (
      <Container className="InvoicePage-container">
        <StyledContainer className="InvoicePage-card">
          <Text className="InvoicePage-card-heading" textStyle="heading-sm">
            Account summary
          </Text>
          <Row hidden={[]}>
            <Col md={12} lg={4}>
              <StyledSummaryBox>
                <Text textStyle="body-bold" asChild>
                  <h6>Current invoice amount</h6>
                </Text>
                <Text textStyle="heading-sm">{numberUtils.asCurrency(currentInvoice.amount)}</Text>
              </StyledSummaryBox>
            </Col>
            <Col md={12} lg={4}>
              <StyledSummaryBox>
                <Text textStyle="body-bold" asChild>
                  <h6>Current balance</h6>
                </Text>
                <Text textStyle="heading-sm">{numberUtils.asCurrency(currentInvoice.balance)}</Text>
              </StyledSummaryBox>
            </Col>
            <Col md={12} lg={4}>
              <StyledSummaryBox>
                <Text textStyle="body-bold" asChild>
                  <h6>Total account balance</h6>
                </Text>
                <Text textStyle="heading-sm">
                  {numberUtils.asCurrencyWithDefault(currentInvoice.totalAccountBalance, '-')}
                </Text>
              </StyledSummaryBox>
            </Col>
          </Row>
        </StyledContainer>
        {renderPaymentHistory()}
        {currentInvoice.zuoraInvoiceItems?.length > 0 && <InvoiceItemDetailsTable currentInvoice={currentInvoice} />}
        {currentInvoice.invoiceType !== ZuoraInvoiceType.MULTIFAMILY && currentInvoice?.sfhLineItems.length > 0 && (
          <SfhBreakdownTable currentInvoice={currentInvoice} />
        )}
        {currentInvoice.invoiceType !== ZuoraInvoiceType.MULTIFAMILY && currentInvoice?.sfhLineItems.length > 0 && (
          <ListingSpotlightTable currentInvoice={currentInvoice} />
        )}
      </Container>
    );
  };

  const handleDownloadClick = () => {
    const invoiceNumbers = [];
    invoiceNumbers.push(invoiceId);
    getInvoices(invoiceNumbers);
  };

  const handleDownloadCsv = () => {
    if (!currentInvoice) return;
    if (isSingleFamilyLegacy) {
      downloadSingleFamilyLegacyCsv(currentInvoice, user);
    } else {
      downloadRocketshipCsv(currentInvoice, user);
    }
  };

  const handlePayInvoiceClick = (invoiceId: string) => {
    history.push(`${paths.billingPage}/${paths.invoicePage}/${invoiceId}/payment`);
  };

  const canDownloadCsv =
    currentInvoice &&
    (currentInvoice.invoiceType === ZuoraInvoiceType.SINGLE_FAMILY_DAILY_LISTINGS ||
      currentInvoice.invoiceType === ZuoraInvoiceType.SINGLE_FAMILY_REIT ||
      currentInvoice.invoiceType === ZuoraInvoiceType.SINGLE_FAMILY_FLAT_FEE) &&
    currentInvoice?.sfhLineItems.length > 0;

  const renderHeader = () => {
    if (!currentInvoice) return;
    return (
      <>
        <div>
          <Text className="InvoicePage-heading" textStyle="heading-sm">
            {currentInvoice.accountName}
          </Text>
          <Box css={{ marginLeft: 'sm' }} asChild>
            <span>
              <TextButton
                onClick={handleDownloadClick}
                disabled={isMaintenanceMode}
                data-eventid={EVENT_IDS.DOWNLOAD_INVOICES}
              >
                Download PDF
              </TextButton>
            </span>
          </Box>
          {canDownloadCsv && (
            <Box css={{ marginLeft: 'sm' }} asChild>
              <span>
                <TextButton onClick={handleDownloadCsv} data-eventid={EVENT_IDS.DOWNLOAD_CSV}>
                  Download CSV
                </TextButton>
              </span>
            </Box>
          )}
        </div>
        <Row hidden={[]}>
          {/* @ts-expect-error fix zrs-comet types */}
          <Col mx={12} md={6}>
            <Box css={{ paddingBottom: 'sm' }}>
              <Box css={{ display: 'flex', justifyContent: 'space-between' }}>
                <Text textStyle="body-bold">Invoice number</Text>
                <Text>{invoiceId}</Text>
              </Box>
            </Box>
            <Box css={{ paddingBottom: 'sm' }}>
              <Box css={{ display: 'flex', justifyContent: 'space-between' }}>
                <Text textStyle="body-bold">Invoice date</Text>
                <Text>
                  {currentInvoice.invoiceLocalDate
                    ? format(localDateToDate(currentInvoice.invoiceLocalDate), 'MM/dd/yyyy')
                    : '-'}
                </Text>
              </Box>
            </Box>
            <Box css={{ paddingBottom: 'sm' }}>
              <Box css={{ display: 'flex', justifyContent: 'space-between' }}>
                <Text textStyle="body-bold">Service period</Text>
                <Text>{billingUtils.calculateServicePeriod(currentInvoice.zuoraInvoiceItems)}</Text>
              </Box>
            </Box>
            <Box css={{ paddingBottom: 'sm' }}>
              <Box css={{ display: 'flex', justifyContent: 'space-between' }}>
                <Text textStyle="body-bold">Last updated</Text>
                <Text>{billingUtils.formatZuoraRefresh(currentInvoice.lastTimeSyncedMs)}</Text>
              </Box>
            </Box>
          </Col>
          {currentInvoice.balance > 0 && !paymentsEnabled && (
            <StyledBalanceCol xs={12} md={6}>
              <Text asChild>
                <p>{`Balance due by ${localDateToDate(currentInvoice.invoiceDueDate).toLocaleDateString()}`}</p>
              </Text>
              <Text className="InvoicePage-amount-due" asChild>
                <p>{numberUtils.asCurrency(currentInvoice.balance)}</p>
              </Text>
              <div onClick={handlePayInfo} className="Utils-primary Invoice-Page-how-to-pay">
                How to pay?
              </div>
            </StyledBalanceCol>
          )}
          {paymentsEnabled && (
            <StyledBalanceCol xs={12} md={6}>
              {currentInvoice.balance > 0 && (
                <Text asChild>
                  <p>
                    {currentInvoice.accountAutopay
                      ? `Next payment date: ${billingUtils.getNextAutoPaymentDate()}`
                      : `Balance due by ${localDateToDate(currentInvoice.invoiceDueDate).toLocaleDateString()}`}
                  </p>
                </Text>
              )}
              <Text asChild>
                <p>
                  Auto Pay:
                  <b>{currentInvoice.accountAutopay ? 'ON' : 'OFF'}</b>{' '}
                  {currentInvoice.canMakePayments && (
                    <TextButton
                      disabled={isMaintenanceMode}
                      onClick={() => handleEditClick(currentInvoice.accountNumber)}
                    >
                      Edit
                    </TextButton>
                  )}
                </p>
              </Text>
              {currentInvoice.balance > 0 && (
                <Text className="InvoicePage-amount-due" asChild>
                  <p>{numberUtils.asCurrency(currentInvoice.balance)}</p>
                </Text>
              )}
              {currentInvoice.canMakePayments && currentInvoice.balance > 0 && (
                <>
                  <Box css={{ marginTop: 'sm' }} />
                  <Button
                    tone="brand"
                    emphasis="filled"
                    onClick={() => handlePayInvoiceClick(currentInvoice.invoiceNumber)}
                    disabled={isMaintenanceMode}
                  >
                    Make a payment
                  </Button>
                </>
              )}
            </StyledBalanceCol>
          )}
        </Row>
      </>
    );
  };

  const renderLoadingHeader = () => (
    <>
      <Row hidden={[]}>
        <Col xs={8}>
          <Text className="InvoicePage-heading" textStyle="heading-sm">
            {invoiceId}
          </Text>
        </Col>
      </Row>
      {currentInvoiceLoadingState !== LoadingConstants.ERROR && (
        <Row hidden={[]}>
          <Col>
            <SpinnerContainer />
          </Col>
        </Row>
      )}
    </>
  );

  const isLoaded = currentInvoiceLoadingState === LoadingConstants.SUCCESS;

  return (
    <AuthCheckWrapper>
      <div
        className="InvoicePage"
        data-eventid={EVENT_IDS.VIEW_RENTALS_BILLING_INVOICE}
        data-eventdescription="View billing invoice page event"
      >
        <HeadWrapper title={DEFAULT_PAGE_TITLE} />
        <PageWrapper
          title=""
          backButton={
            <TextButton asChild icon={<IconChevronLeftFilled />}>
              <Link to={queryParams.prev === 'paid' ? '/rental-manager/billing/paid' : '/rental-manager/billing/due'}>
                Back
              </Link>
            </TextButton>
          }
        >
          <BillingPortalMaintenanceBanner />
          <StyledContainer className="InvoicePage-info-row">
            {isLoaded ? renderHeader() : renderLoadingHeader()}
          </StyledContainer>
          {currentInvoiceErrorMessage && <BillingError errorMsg={currentInvoiceErrorMessage} />}
          {isLoaded && renderInvoiceDetails()}
        </PageWrapper>
        {showPaymentInfoModal && <PaymentInfoModal onClose={() => setShowPaymentInfoModal(false)} />}
      </div>
    </AuthCheckWrapper>
  );
};

export default withErrorBoundary(InvoicePage);
